/**
 * Cyphex Teams — presence & custom status.
 * Mirrors infra/sql/008_presence.sql.
 */

import type { IsoDateTime, TimeZone, Uuid, UserId } from './common';

/**
 * The 9 AUTOMATIC states. Computed server-side from connection activity,
 * calendar, and live call/meeting membership. A client may never set these.
 */
export type AutoPresenceState =
  | 'available'
  | 'busy'
  | 'in_a_meeting'
  | 'in_a_call'
  | 'presenting'
  | 'away'
  | 'do_not_disturb'
  | 'out_of_office'
  | 'offline';

/** Manual overrides a user picks from the avatar menu. */
export type ManualPresenceState =
  | 'appear_available'
  | 'appear_busy'
  | 'appear_dnd'
  | 'be_right_back'
  | 'appear_away'
  | 'appear_offline';

export type PresenceState = AutoPresenceState | ManualPresenceState;

/**
 * The value clients render. Manual overrides are already resolved to their
 * automatic equivalent server-side, so the UI only ever switches on 9 cases
 * plus `be_right_back`.
 */
export type EffectivePresenceState = AutoPresenceState | 'be_right_back';

/** Presence dot colour mapping is a CLIENT concern; documented here for parity. */
export const PRESENCE_DOT: Record<EffectivePresenceState, 'green' | 'red' | 'amber' | 'gray' | 'purple'> = {
  available: 'green',
  busy: 'red',
  in_a_meeting: 'red',
  in_a_call: 'red',
  presenting: 'red',
  do_not_disturb: 'red',
  away: 'amber',
  be_right_back: 'amber',
  out_of_office: 'purple',
  offline: 'gray',
};

/**
 * THE APP STATES: the members of `presence_state` that say what the APP is doing
 * rather than what the PERSON is. Exactly the set `cyphex.presence_activity_of()`
 * produces (infra/sql/062), in its precedence order, least-available first.
 */
export const APP_ACTIVITY_STATES = ['presenting', 'in_a_call', 'in_a_meeting'] as const;

export type AppActivityState = (typeof APP_ACTIVITY_STATES)[number];

const APP_ACTIVITY_SET: ReadonlySet<string> = new Set(APP_ACTIVITY_STATES);

/**
 * THE ACTIVITY A RESOLVED STATE IMPLIES, or `null`.
 *
 * infra/sql/068 returns an activity VERBATIM as `effective_state`, so a resolved
 * state inside the set above IS the activity. That makes this a sound derivation
 * — not a guess — for the producers that hold the resolved value but not the
 * column: the gateway, which has sockets rather than participant rows, and every
 * fixture and mock, which has no database at all.
 *
 * It under-reports in exactly one case, harmlessly. A manual SUPPRESSION chosen
 * during a call resolves to `do_not_disturb` (068 keeps suppressions above the
 * activity tier), so this answers `null` — and both the SQL resolver and its
 * client mirror reach that same `do_not_disturb` from the override alone. The
 * two still agree, which is the property that matters.
 *
 * DO NOT use it where the real column is to hand. `toSummary` in
 * apps/api/src/services/presence.ts reads `user_presence.activity_state`
 * directly, because that is the truth and this is only an inference from it.
 */
export function activityOfState(
  state: EffectivePresenceState | null | undefined,
): AppActivityState | null {
  return state != null && APP_ACTIVITY_SET.has(state) ? (state as AppActivityState) : null;
}

/** Custom status: emoji + message + expiry. */
export interface CustomStatus {
  emoji: string | null;
  text: string | null;
  expiresAt: IsoDateTime | null;
  /** Show the text to anyone who DMs or @mentions this user. */
  pinInChat: boolean;
}

/** Compact presence embedded in UserRef, rosters, and the People-online card. */
export interface PresenceSummary {
  state: EffectivePresenceState;
  /** When the current state began. Drives "Away for 12m". */
  since: IsoDateTime;
  /**
   * When this person was last actually here — the moment their last device went,
   * not the moment anything noticed. Drives "Away · Last seen 15 minutes ago".
   *
   * It belongs on the SUMMARY, not only on the full `Presence`, because every
   * surface that shows OTHER people is served summaries: `/presence/bulk`, the
   * `presence` embedded in `UserRef`, and the gateway's `presence.update` frame.
   * While it lived on `Presence` alone the API simply never sent it, the client
   * had nothing to render, and the one component that draws it fell back to the
   * time the snapshot was parsed — a "last seen" measured from the page load.
   *
   * `null` when nothing has ever recorded a departure for this person. WHETHER
   * TO SHOW IT is not decided here: it is a fact about the person, and the one
   * component that renders it suppresses it for every state where the person is
   * demonstrably present. Deciding it twice is how the two would drift.
   */
  lastActivityAt?: IsoDateTime | null;
  /**
   * WHAT THE APP IS DOING — In a call, Presenting, In a meeting — exactly as
   * `cyphex.user_presence.activity_state` holds it (infra/sql/062: derived by
   * trigger from the participant tables, "Never client-set"). `null` is the
   * normal case and means "nothing worth reporting", NOT "available".
   *
   * It belongs on the SUMMARY because it is an INPUT to the resolver, and the
   * web client mirrors that resolver branch for branch. While the field did not
   * exist the client had nowhere to put the activity, went looking for it inside
   * `autoState` — where the server never puts it, because `auto_state` and
   * `activity_state` are separate columns — and resolved a user who was
   * demonstrably on a call to `available`. A green tick, mid-call, to himself.
   *
   * REQUIRED RATHER THAN OPTIONAL, AND THAT IS THE POINT: every producer of a
   * summary must say something here, so a new one cannot quietly omit it and
   * leave the client reading a silent `null`.
   *
   * KEEPING IT CONSISTENT WITH `state` IS THE PRODUCER'S JOB. `mergePresence`
   * clears it in the branch where tier 1 overrules the durable row, because a
   * client that resolved `in_a_call` from an activity the server had just
   * decided to disbelieve would contradict the `state` sent beside it.
   */
  activityState: AutoPresenceState | null;
  status?: CustomStatus | null;
  /** Free text under the name in the People-online card. */
  statusLine?: string | null;
}

export interface OutOfOffice {
  enabled: boolean;
  message: string | null;
  startsAt: IsoDateTime | null;
  endsAt: IsoDateTime | null;
  /** Also auto-reply to senders outside the tenant. */
  external: boolean;
}

/** Full presence record returned by GET /presence/me. */
export interface Presence extends PresenceSummary {
  userId: UserId;
  autoState: AutoPresenceState;
  manualState: ManualPresenceState | null;
  manualExpiresAt: IsoDateTime | null;
  connectionCount: number;
  lastActivityAt: IsoDateTime | null;
  outOfOffice: OutOfOffice;
  autoAwayAfterMinutes: number;
  dndDuringMeetings: boolean;
  dndDuringCalls: boolean;
}

/** PATCH /presence/me */
export interface UpdatePresenceRequest {
  manualState?: ManualPresenceState | null;
  /** Minutes until the override auto-resets. `null` = never. */
  resetAfterMinutes?: number | null;
  status?: Partial<CustomStatus> | null;
  outOfOffice?: Partial<OutOfOffice>;
  autoAwayAfterMinutes?: number;
  dndDuringMeetings?: boolean;
  dndDuringCalls?: boolean;
}

/**
 * Batch presence lookup. Capped at 500 ids per request; the client should
 * subscribe over the WebSocket for updates rather than polling this.
 */
export interface BulkPresenceRequest {
  userIds: UserId[];
}

export interface BulkPresenceResponse {
  presence: Record<Uuid, PresenceSummary>;
}

export interface TimeZoneAwarePresence extends PresenceSummary {
  timezone: TimeZone;
  /** Local time for the subject, so the UI can warn "it's 2am for them". */
  localTime: string;
}
